import React from "react";

import { PageBuilderOpeningHeroFragment } from "types/graphql-types";
import { graphql } from "gatsby";
import {
  ButtonsContainer,
  HeroBanner,
  HeroBannerRightPanel,
  HeroBannerTitle,
  HeroBannerAction,
  HeroBannerContent,
  isSanityImagePresent,
  HeroBannerLeftPanel
} from "@silversea-ssc/ui-components-react";
import PageBuilderButtonCTA from "./PageBuilderButtonCTA";
import { isPresent } from "src/utils/checks";
import BoxedOpeningHero from "components/BoxedOpeningHero/BoxedOpeningHero";
import NativeHeroImage from "components/NativeHeroImage/NativeHeroImage";
import CalligraphicOpeningHero from "components/CalligraphicOpeningHero/CalligraphicOpeningHero";
import { useRemotePromo } from "src/ssc-api/useRemotePromos";
import { dateFromOptionalDate } from "src/utils/date";
import { forceResTimezone } from "src/utils/date/forceResTimezone";
import { useLingui } from "@lingui/react/macro";
const Countdown = React.lazy(() => import("components/Countdown/Countdown"));

type Props = PageBuilderOpeningHeroFragment;

const HeroBannerWithTitle: React.FC<React.PropsWithChildren<Props>> = ({
  background,
  title,
  heroContent,
  ctaButtons
}) => {
  const imageComponent = isSanityImagePresent(background) ? (
    <NativeHeroImage
      image={background}
      alt={background?.alt?.localized || title?.localized || ""}
    />
  ) : undefined;

  const hasCTAButtons = ctaButtons && ctaButtons.length > 0;

  return (
    <HeroBanner
      backgroundImageComponent={imageComponent}
      loading={undefined}
      overlay="right"
    >
      <HeroBannerRightPanel>
        {(title?.localized?.trim()?.length || 0) > 0 && (
          <HeroBannerTitle>{title?.localized}</HeroBannerTitle>
        )}
        {heroContent?.localized && (
          <HeroBannerContent>{heroContent.localized}</HeroBannerContent>
        )}
        {hasCTAButtons && (
          <HeroBannerAction>
            <ButtonsContainer align="left" mobileFullSize>
              {ctaButtons.map((b) => (
                <PageBuilderButtonCTA key={b?._key} forHero="inverted" {...b} />
              ))}
            </ButtonsContainer>
          </HeroBannerAction>
        )}
      </HeroBannerRightPanel>
    </HeroBanner>
  );
};

const LeftHeroBannerWithTitle: React.FC<React.PropsWithChildren<Props>> = ({
  background,
  title,
  heroContent,
  ctaButtons,
  countdownPromo,
  countdownLabel
}) => {
  const { t } = useLingui();
  const promoData = useRemotePromo(countdownPromo?.configuration?.code);
  const imageComponent = isSanityImagePresent(background) ? (
    <NativeHeroImage
      image={background}
      alt={background?.alt?.localized || title?.localized || ""}
    />
  ) : undefined;

  const countdownExpireDate = promoData?.data?.toDate
    ? dateFromOptionalDate(promoData.data.toDate)
    : undefined;

  const hasCTAButtons = ctaButtons && ctaButtons.length > 0;
  const hasCountdown = countdownExpireDate && countdownExpireDate > new Date();

  const hasActions = hasCTAButtons || hasCountdown;

  return (
    <HeroBanner
      backgroundImageComponent={imageComponent}
      loading={undefined}
      overlay="left"
    >
      <HeroBannerLeftPanel size="large">
        {(title?.localized?.trim()?.length || 0) > 0 && (
          <HeroBannerTitle>{title?.localized}</HeroBannerTitle>
        )}
        {heroContent?.localized && (
          <HeroBannerContent>{heroContent.localized}</HeroBannerContent>
        )}
        {hasActions && (
          <HeroBannerAction>
            <ButtonsContainer align="left">
              {hasCountdown && (
                <React.Suspense fallback={null}>
                  <Countdown
                    expireDate={forceResTimezone(countdownExpireDate)}
                    label={
                      countdownLabel?.localized ??
                      countdownPromo?.countdownLabel?.localized ??
                      t`OFFER ENDS IN`
                    }
                  />
                </React.Suspense>
              )}
              {hasCTAButtons &&
                ctaButtons.map((b) => (
                  <PageBuilderButtonCTA
                    key={b?._key}
                    forHero="inverted"
                    {...b}
                  />
                ))}
            </ButtonsContainer>
          </HeroBannerAction>
        )}
      </HeroBannerLeftPanel>
    </HeroBanner>
  );
};

const HeroRightBoxedBannerWithTitle: React.FC<
  React.PropsWithChildren<Props>
> = ({ background, title, heroContent, ctaButtons }) => {
  return (
    <BoxedOpeningHero
      image={background}
      title={title?.localized}
      subtitle={heroContent?.localized}
      ctaButtons={ctaButtons?.filter(isPresent)}
    />
  );
};

const PageBuilderOpeningHero: React.FC<React.PropsWithChildren<Props>> = (
  props
) => {
  const { calligraphicTitle } = props;

  const withCalligraphicTitle =
    isPresent(calligraphicTitle) && isSanityImagePresent(calligraphicTitle);

  return (
    <React.Fragment>
      {withCalligraphicTitle ? (
        <CalligraphicOpeningHero {...props} />
      ) : props.variant == "boxed" ? (
        <HeroRightBoxedBannerWithTitle {...props} />
      ) : props.variant == "left" ? (
        <LeftHeroBannerWithTitle {...props} />
      ) : (
        <HeroBannerWithTitle {...props} />
      )}
    </React.Fragment>
  );
};

export default PageBuilderOpeningHero;

export const fragments = graphql`
  fragment PageBuilderOpeningHero on SanityOpeningHero {
    _key
    variant
    title {
      localized
    }

    #NOTE: this is intended, for some reason there is a type/name conflict
    heroContent: content {
      localized
    }

    background {
      ...HeroResponsiveImageWithAlt
      asset {
        url
      }
    }

    ctaButtons {
      ...ButtonCTA
    }

    countdownPromo {
      configuration {
        code
      }
      countdownLabel {
        localized
      }
    }

    countdownLabel {
      localized
    }

    calligraphicTitle {
      ...Image
    }

    calligraphicTitlePosition
  }
`;
